import { storeToRefs } from 'pinia';
import { onMounted, onUnmounted } from 'vue';
import { getRenderedBookViewerElement } from '../util/book';
import useRenditionStore from '../stores/rendition';
import useHighlightsStore from '../stores/highlights';
import { ViewerSize } from '../types';
import debounce from 'lodash/debounce';

const useContent = () => {
  const renditionStore = useRenditionStore();
  const { isReflowable, isInitialRendering } = storeToRefs(renditionStore);
  const { resize, handleBookLocations: handleBookLocationsInRenditionStore } = renditionStore;
  const { reregisterAllHighlights } = useHighlightsStore();

  let isOrientationChangeInProgress = false;

  const handleBookLocations = async (forceGenerate = false) => {
    await handleBookLocationsInRenditionStore(forceGenerate);
  };

  // Re-register annotation styling after epub.js finishes its internal re-render post-resize.
  // Fires 300ms after handleResize — long enough for epub.js to complete its re-render pipeline.
  const reregisterAnnotationsAfterResize = debounce(() => {
    reregisterAllHighlights();
  }, 300);

  const handleResize = (size?: ViewerSize) => {
    if (isInitialRendering.value) {
      console.warn('Blocking debounced resize due to initial render in progress.');
      // If the size is different from the size used for renderBook, we queue it manually.
      return;
    }

    const viewer = getRenderedBookViewerElement();

    if (viewer) {
      // if book is reflowable
      if (isReflowable.value) {
        viewer.classList.add('inner-pages');
      }

      let { height, width } = size ?? { height: viewer.offsetHeight, width: viewer?.offsetWidth };

      if (
        (isOrientationChangeInProgress || viewer.offsetHeight > window.innerHeight) &&
        window.innerWidth &&
        window.innerHeight
      ) {
        height = window.innerHeight;
        width = window.innerWidth;
      }

      resize(width, height);
      reregisterAnnotationsAfterResize();
    }
  };

  const onReSizeViewer = debounce(handleResize, 500);

  const handleOrientationChange = () => {
    isOrientationChangeInProgress = true;

    setTimeout(() => {
      onReSizeViewer();
      setTimeout(() => {
        isOrientationChangeInProgress = false;
      }, 100);
    }, 300);
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      reregisterAllHighlights();
    }
  };

  const registerListeners = () => {
    onMounted(() => {
      window.addEventListener('orientationchange', handleOrientationChange);
      document.addEventListener('visibilitychange', onVisibilityChange);

      if (navigator.userAgent.match(/iPhone/i) && window.innerWidth > window.innerHeight && window.innerWidth < 736) {
        setTimeout(() => {
          window.scrollTo(0, 100);
        }, 2000);
      }
    });

    onUnmounted(() => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    });
  };

  return {
    registerListeners,
    handleResize,
    handleOrientationChange,
    handleBookLocations,
  };
};

export default useContent;
